import type { ResolvedTheme } from '../shared/theme-core';
import { BLACK, DEFAULT_THEME, SHADOW_INK, STATUS_COLORS, THEME_TOKENS, WHITE, adaptToSurface, enforceContrast, luminance, mix, parseHex, readableOn, rgba, toHex } from '../shared/theme-core';
import { LIBRARY_PRESETS, type PresetCategory } from './theme-presets';

export { PRESET_CATEGORIES, INDUSTRIES, INDUSTRY_CATEGORIES } from './theme-presets';
export type { PresetCategory, Industry } from './theme-presets';

/**
 * The application's view of the palette: everything in `src/shared/theme-core`,
 * plus the presets and the one function that touches the DOM. The core is
 * shared with the Functions, which serve the client-facing page and must
 * resolve the same colours from the same document.
 */
export * from '../shared/theme-core';

export interface ThemePreset {
  id: string;
  name: string;
  description: string;
  /** The mood it belongs to, which is how the picker groups them. */
  category: PresetCategory;
  theme: ResolvedTheme;
}

/**
 * Starting points, not a menu of the only allowed answers.
 *
 * Most people want something coherent in one click and then one colour changed;
 * building a palette token by token from a monochrome default is how you end up
 * with an interface nobody wants to look at for six hours a day.
 */
const HOUSE_PRESETS: readonly ThemePreset[] = [
  {
    id: 'monochrome',
    category: 'monochromatic',
    name: 'Monochrome',
    description: 'The default. Neutral greys, ink-black controls.',
    theme: DEFAULT_THEME,
  },
  {
    id: 'ocean',
    category: 'tranquil',
    name: 'Ocean',
    description: 'Cool greys with a blue accent.',
    theme: {
      accent: '#2563eb',
      ink: '#0f172a',
      paper: '#ffffff',
      canvas: '#eaeff5',
      canvasTint: '#f4f7fa',
      line: '#dde5ee',
      lineStrong: '#bcc9d8',
      textMuted: '#64748b',
      textFaint: '#8293ac',
    },
  },
  {
    id: 'sand',
    category: 'neutral',
    name: 'Sand',
    description: 'Warm paper, brown-black controls.',
    theme: {
      accent: '#b4620f',
      ink: '#1c1917',
      paper: '#fffdfb',
      canvas: '#f4efe9',
      canvasTint: '#faf6f2',
      line: '#e8e0d8',
      lineStrong: '#d0c4b8',
      textMuted: '#78706a',
      textFaint: '#9b9087',
    },
  },
  {
    id: 'forest',
    category: 'tranquil',
    name: 'Forest',
    description: 'Green-grey, low glare for long sessions.',
    theme: {
      accent: '#15803d',
      ink: '#14201a',
      paper: '#ffffff',
      canvas: '#e9efea',
      canvasTint: '#f4f8f5',
      line: '#dde6e0',
      lineStrong: '#bfcdc4',
      textMuted: '#5f6f66',
      textFaint: '#85968d',
    },
  },
  {
    id: 'plum',
    category: 'romantic',
    name: 'Plum',
    description: 'Cool violet neutrals.',
    theme: {
      accent: '#7c3aed',
      ink: '#1c1524',
      paper: '#ffffff',
      canvas: '#efecf4',
      canvasTint: '#f7f5fa',
      line: '#e5e0ed',
      lineStrong: '#c8c0d6',
      textMuted: '#6b6377',
      textFaint: '#978fa4',
    },
  },
  {
    id: 'rose',
    category: 'romantic',
    name: 'Rose',
    description: 'Warm pink-grey, soft contrast.',
    theme: {
      accent: '#be185d',
      ink: '#231419',
      paper: '#fffdfd',
      canvas: '#f4ecef',
      canvasTint: '#fbf6f8',
      line: '#ecdfe4',
      lineStrong: '#d6bfc8',
      textMuted: '#77636a',
      textFaint: '#a18c95',
    },
  },
  {
    id: 'slate',
    category: 'neutral',
    name: 'Slate',
    description: 'Cooler and flatter than the default.',
    theme: {
      accent: '#0f766e',
      ink: '#1e252b',
      paper: '#ffffff',
      canvas: '#e8ecef',
      canvasTint: '#f3f6f8',
      line: '#dde3e8',
      lineStrong: '#bcc6ce',
      textMuted: '#61707c',
      textFaint: '#8595a0',
    },
  },
  {
    id: 'paper',
    category: 'neutral',
    name: 'Paper',
    description: 'Near-white and almost borderless.',
    theme: {
      accent: '#111827',
      ink: '#111827',
      paper: '#ffffff',
      canvas: '#f7f7f6',
      canvasTint: '#fbfbfa',
      line: '#ededea',
      lineStrong: '#d6d6d1',
      textMuted: '#6b6b66',
      textFaint: '#92928b',
    },
  },
  {
    id: 'midnight',
    category: 'monochromatic',
    name: 'Midnight',
    description: 'Dark surfaces, light text and controls.',
    theme: {
      accent: '#6ea8fe',
      ink: '#ecedee',
      paper: '#17191a',
      canvas: '#0e0f10',
      canvasTint: '#1e2021',
      line: '#262a2b',
      lineStrong: '#3a3f41',
      textMuted: '#9aa2a6',
      textFaint: '#6d7478',
    },
  },
  {
    id: 'graphite',
    category: 'monochromatic',
    name: 'Graphite',
    description: 'Dark, but softer than Midnight.',
    theme: {
      accent: '#d4d4d8',
      ink: '#f4f4f5',
      paper: '#1f1f22',
      canvas: '#161618',
      canvasTint: '#27272b',
      line: '#2f2f34',
      lineStrong: '#46464d',
      textMuted: '#a1a1aa',
      textFaint: '#71717a',
    },
  },
  {
    id: 'deep-sea',
    category: 'tranquil',
    name: 'Deep sea',
    description: 'Dark with a blue cast.',
    theme: {
      accent: '#38bdf8',
      ink: '#e2e8f0',
      paper: '#111c2b',
      canvas: '#0a121c',
      canvasTint: '#17253a',
      line: '#1e2f47',
      lineStrong: '#31465f',
      textMuted: '#94a3b8',
      textFaint: '#64748b',
    },
  },
  {
    id: 'espresso',
    category: 'neutral',
    name: 'Espresso',
    description: 'Dark and warm.',
    theme: {
      accent: '#e0a458',
      ink: '#f5efe8',
      paper: '#221c17',
      canvas: '#171210',
      canvasTint: '#2c241d',
      line: '#342a22',
      lineStrong: '#4d4034',
      textMuted: '#ab9c8d',
      textFaint: '#7a6d61',
    },
  },
  {
    id: 'nocturne',
    category: 'monochromatic',
    name: 'Nocturne',
    description: 'Dark violet, higher contrast.',
    theme: {
      accent: '#a78bfa',
      ink: '#ede9f5',
      paper: '#1b1626',
      canvas: '#120e1a',
      canvasTint: '#251e33',
      line: '#2c2440',
      lineStrong: '#453a5e',
      textMuted: '#a196b8',
      textFaint: '#736a86',
    },
  },
  {
    id: 'high-contrast',
    category: 'monochromatic',
    name: 'High contrast',
    description: 'Maximum legibility. Pure black on pure white.',
    theme: {
      accent: '#0000cc',
      ink: '#000000',
      paper: '#ffffff',
      canvas: '#e8e8e8',
      canvasTint: '#f4f4f4',
      line: '#c9c9c9',
      lineStrong: '#8a8a8a',
      textMuted: '#3d3d3d',
      textFaint: '#5c5c5c',
    },
  },
];

/** The house presets first, then the library, in the order the picker shows them. */
export const THEME_PRESETS: readonly ThemePreset[] = [...HOUSE_PRESETS, ...LIBRARY_PRESETS];

/**
 * Writes the palette onto an element as custom properties.
 *
 * Five more properties are derived rather than exposed: the two ink state
 * layers, the two text colours that sit on ink, and the shadow. Letting a
 * tenant set those independently is how you get a button whose hover state is
 * lighter than its label.
 */
export function applyTheme(root: HTMLElement, theme: ResolvedTheme): void {
  const legible = enforceContrast(theme);
  for (const spec of THEME_TOKENS) {
    root.style.setProperty(spec.cssVar, legible[spec.key]);
  }

  const ink = parseHex(legible.ink) ?? BLACK;
  const canvas = parseHex(legible.canvas) ?? WHITE;
  const onInk = readableOn(ink);

  // Hover and press shift ink toward whatever sits on top of it, so the
  // direction is right whether ink is near-black or near-white.
  root.style.setProperty('--ink-soft', toHex(mix(ink, onInk, 0.07)));
  root.style.setProperty('--ink-press', toHex(mix(ink, onInk, 0.13)));
  root.style.setProperty('--on-ink', toHex(onInk));
  root.style.setProperty('--on-ink-muted', toHex(mix(onInk, ink, 0.38)));
  root.style.setProperty('--state-hover', rgba(ink, 0.06));
  root.style.setProperty('--state-press', rgba(ink, 0.1));

  // A shadow tuned for white paper disappears on a dark background, so it
  // deepens as the canvas darkens.
  const dark = luminance(canvas) < 0.2;

  /**
   * Status colours, and the one that is not really a status.
   *
   * Alert, stop, urgent and normal keep their hue in every organisation:
   * red has to mean the same thing in a support conversation whichever
   * workspace it happens in, and no tenant palette is allowed to make a
   * warning look calm.
   *
   * "Running" is the exception, and was the odd one out on every palette:
   * a timer that is running is a state, not a warning, and printing it in a
   * fixed amber left one stubbornly brown pill on a workspace whose whole
   * palette was blue. It follows the accent now, adapted for legibility like
   * everything else, and falls back to the amber when a workspace has no
   * accent of its own. Recorded in docs/decisions.md as D58.
   */
  const surface = parseHex(legible.paper) ?? WHITE;
  const accent = parseHex(legible.accent);
  const statuses: Record<string, string> = {
    ...STATUS_COLORS,
    ...(accent ? { running: legible.accent } : {}),
  };
  for (const [name, value] of Object.entries(statuses)) {
    const adapted = adaptToSurface(value, surface);
    root.style.setProperty(`--${name}`, adapted);
    // The tint behind a status — the running-timer pill, the destructive
    // button — is mixed with the surface rather than laid over it with alpha,
    // so it is a real colour on both a white and a near-black paper.
    const parsed = parseHex(adapted) ?? surface;
    const tint = mix(surface, parsed, dark ? 0.22 : 0.12);
    root.style.setProperty(`--${name}-tint`, toHex(tint));
    root.style.setProperty(`--${name}-line`, toHex(mix(surface, parsed, dark ? 0.5 : 0.4)));
    // What is legible printed *on* the status colour, for a filled control
    // such as the timer's stop button.
    root.style.setProperty(`--on-${name}`, toHex(readableOn(parsed)));
    // And on the tint, for the destructive button and the error panel. Adapting
    // against paper is not enough: the label sits on the tint, which is a
    // different and closer colour, and measured 3.53:1 on the dark preset.
    root.style.setProperty(`--${name}-on-tint`, adaptToSurface(adapted, tint));
  }

  // The scrim behind a dialog must always darken. Deriving it from ink would
  // make it a white wash the moment ink is light, which brightens the page
  // behind the dialog instead of pushing it back.
  root.style.setProperty('--scrim', rgba(BLACK, dark ? 0.66 : 0.4));

  /**
   * The spark: the dot beside the active sidebar item, the one drop of
   * colour in the shell.
   *
   * It was a fixed scarlet, on the reasoning that punctuation is not a tenant
   * colour. In a workspace that had chosen a palette it read as a mistake —
   * the single red thing on a page with no other red in it. It is the accent
   * now, adapted to whatever surface it is printed on so it stays visible on
   * a dark sidebar and a pale one alike, and stays scarlet only when a
   * workspace has set no accent.
   */
  const sparkBase = legible.accent && parseHex(legible.accent) ? legible.accent : '#e4002b';
  const spark = adaptToSurface(sparkBase, surface, 3);
  root.style.setProperty('--spark', spark);
  root.style.setProperty('--on-spark', toHex(readableOn(parseHex(spark) ?? BLACK)));
  // Flat by design: a card is a hairline in the palette's line colour, and a
  // menu a firmer one. On a dark palette the hairline lightens with the line.
  root.style.setProperty('--shadow-card', `0 0 0 1px ${legible.line}`);
  root.style.setProperty('--shadow-menu', `0 0 0 1px ${legible.lineStrong}`);

  // `color-scheme` is what makes native controls — scrollbars, the date picker,
  // the colour swatch — follow the theme instead of staying stubbornly light.
  root.style.colorScheme = dark ? 'dark' : 'light';
}
